import type { Role } from '@/types';
import { ROLES } from '@/types';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const isKnownRole = (role: string): role is Role => (ROLES as readonly string[]).includes(role);

/** Last resort for a wire value that is not in `ROLES` yet — a role the backend added ahead of the
 *  frontend reads as "Lab Manager" rather than leaking `lab_manager`. */
function titleCaseRole(role: string): string {
  return role
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** A hook, not a plain function, because the labels are i18n resources now — so a role option list
 *  has to be built inside a component rather than at module scope, where no translator exists. */
export function useRoleLabel(): (role: string) => string {
  const { t } = useTranslation('roles');

  return useCallback(
    (role: string) => {
      if (!isKnownRole(role)) return titleCaseRole(role);

      // i18next returns the key when it cannot resolve one, so a role in ROLES whose `roles` entry
      // is missing degrades the same way an unknown one does rather than leaking `lab_manager`.
      const label = t(role);
      return label === role ? titleCaseRole(role) : label;
    },
    [t],
  );
}
